// ** React Imports
import { useEffect, useRef, useState } from 'react'

// ** Next Import
import { useRouter } from 'next/router'

// ** MUI Import
import Box from '@mui/material/Box'

import { CLIENTE_ADSENSE, SLOTS, anunciosPermitidos } from 'src/data/general/ads'

/**
 * Un bloque de anuncio de AdSense.
 *
 * Tres cosas que no son obvias y que estan resueltas aca:
 *
 * 1. El sitio es una SPA exportada estatica. `adsbygoogle.push({})` se dispara una
 *    vez por <ins>, no por carga de pagina: al navegar entre herramientas sin
 *    recargar, un bloque montado de nuevo necesita su propio push. Por eso el
 *    componente lleva `key` de ruta y empuja en el efecto de montaje.
 *
 * 2. Empujar dos veces sobre el mismo <ins> tira
 *    "TagError: adsbygoogle.push() error: All ins elements ... already have ads".
 *    En desarrollo, React 19 en modo estricto monta cada componente dos veces, asi
 *    que el guardia de `yaEmpujado` no es teorico.
 *
 * 3. El alto se reserva desde el primer pintado. Sin eso el anuncio aparece tarde y
 *    empuja el contenido hacia abajo, que es exactamente el salto de layout que el
 *    sitio ya habia eliminado (CLS 0,85 -> 0,005).
 *
 * Si el slot no esta configurado en src/data/general/ads.js no se renderiza nada:
 * ni el hueco. Un <ins> sin slot valido no muestra anuncio y AdSense igual lo
 * contabiliza como impresion vacia, lo que hunde el porcentaje de cobertura.
 */
const AdSlot = ({ slot, formato = 'auto', altoMinimo = 280, sx }) => {
  const router = useRouter()
  const ruta = router?.asPath ?? '/'
  const idSlot = SLOTS[slot]
  const ref = useRef(null)
  const empujadoEn = useRef(null)

  /*
    AdSense escribe data-ad-status="filled" o "unfilled" en el <ins> cuando responde.
    Sin mirarlo, un bloque sin relleno deja el alto reservado igual: medido en
    produccion, 390 px de hueco en blanco al pie de cada herramienta. Reservar el
    alto sirve para que el anuncio no empuje el contenido; no sirve para dejar un
    agujero cuando no hay anuncio.

    El desplazamiento al plegarlo no cuenta como CLS en la practica: los dos bloques
    estan al final del texto, y para cuando Google responde el usuario todavia esta
    arriba de la pagina.
  */
  const [relleno, setRelleno] = useState(null)

  useEffect(() => {
    if (!idSlot || empujadoEn.current === ruta || !ref.current) return

    try {
      ;(window.adsbygoogle = window.adsbygoogle || []).push({})
      empujadoEn.current = ruta
    } catch {
      // Sin conexion, con bloqueador o con el cargador aun sin llegar: el bloque se
      // queda vacio y la pagina sigue. No hay nada que reintentar.
    }
  }, [idSlot, ruta])

  useEffect(() => {
    const ins = ref.current
    if (!idSlot || !ins) return

    setRelleno(ins.getAttribute('data-ad-status'))

    const observador = new MutationObserver(() => setRelleno(ins.getAttribute('data-ad-status')))
    observador.observe(ins, { attributes: true, attributeFilter: ['data-ad-status'] })

    return () => observador.disconnect()
  }, [idSlot, ruta])

  if (!idSlot || !anunciosPermitidos(ruta)) return null

  // Sin anuncio que mostrar, el bloque no ocupa nada. Se deja montado —no return
  // null— porque desmontarlo perderia el <ins> que el observador esta mirando.
  const vacio = relleno === 'unfilled'

  return (
    <Box
      sx={{
        display: vacio ? 'none' : 'block',
        minHeight: vacio ? 0 : altoMinimo,
        my: vacio ? 0 : 6,
        overflow: 'hidden',
        // El contenedor es el que reserva el alto; el <ins> crece dentro
        textAlign: 'center',
        ...sx
      }}
    >
      {/*
        key por ruta: al navegar sin recarga React reemplaza el nodo en vez de
        reusarlo. Un <ins> que ya sirvio un anuncio no admite un segundo push, asi
        que sin esto la segunda herramienta que visita el usuario se queda con el
        anuncio de la primera.
      */}
      <ins
        key={ruta}
        ref={ref}
        className='adsbygoogle'
        style={{ display: 'block', minHeight: vacio ? 0 : altoMinimo }}
        data-ad-client={CLIENTE_ADSENSE}
        data-ad-slot={idSlot}
        data-ad-format={formato}
        data-full-width-responsive='true'
      />
    </Box>
  )
}

export default AdSlot
